import { fetch } from 'src/service/api/fetcher'
import { useMutation } from '@tanstack/react-query'
import { useMutationOptionInterface, useMutationReturnData } from 'src/types/react-query'
import {
  AutoLoginSpace,
  CancelSpace,
  CouponCancelValidSpace,
  CouponRefundSpace,
  FavoriteStarSpace,
  GiftPaymentWindowRequestCreditCardSpace,
  GiftReceiveAcceptSpace,
  GiftReceiveRejectSpace,
  GiftRefundSpace,
  GiftSendCancelSpace,
  GiftSendPurchasedSpace,
  JoinSpace,
  JoinWithOtpCertSubmitSpace,
  KakaoMapInfoAuthStatusSpace,
  KakaoMapInfoAuthUpdateSpace,
  KTMVNOCheckSpace,
  LogoutSpace,
  MajorNoticeSpace,
  NotifyUpdateSpace,
  OtpCertRequestSpace,
  OtpCertSubmitSpace,
  PassCertResponseSpace,
  PassDataEncryptRequestSpace,
  PaymentGatewayRequestSpace,
  PaymentWindowRequestCreditCardSpace,
  PaymentWindowResponseSpace,
  PurchaseCancelSpace,
  PurchaseValidCheckSpace,
  PushTokenUpdateSpace,
  RecommendSearchWordListSpace,
  RefundTelecomCertSubmitFileSpace,
  ResponseDataType,
  SelfRegisterCouponCreateSpace,
  SelfRegisterCouponDeleteSpace,
  SelfRegisterCouponImageDeleteSpace,
  SelfRegisterCouponImageUploadSpace,
  SelfRegisterCouponUpdateSpace,
  VersionCheckSpace,
  VulerableCheckSpace,
} from 'src/types/api'
import { CMDTYPES } from 'src/service/constant/cmdtypes'
import { queryClient } from 'src/router'

export const useCustomMutation = <T, U>(
  option: useMutationOptionInterface<T, U>,
): useMutationReturnData<T, U> => {
  const { mutate, mutateAsync, isPending, isSuccess, data } = useMutation<
    ResponseDataType<U>,
    Error,
    T
  >({ ...option, networkMode: 'offlineFirst' })

  return { mutate, mutateAsync, isSuccess, isPending, data }
}
// 앱 버전 체크
export const versionCheckQueryOption = () => ({
  mutationKey: ['version_check'],
  mutationFn: async (data?: VersionCheckSpace.Request) =>
    await fetch.post<VersionCheckSpace.Request, VersionCheckSpace.Response>(
      CMDTYPES.VersionCheck,
      data,
    ),
})

// 자동로그인
export const autoLoginQueryOption = () => ({
  mutationKey: ['auto_login'],
  mutationFn: async (data?: AutoLoginSpace.Request) =>
    await fetch.post<AutoLoginSpace.Request, AutoLoginSpace.Response>(CMDTYPES.AutoLogin, data),
})

// 필수 공지사항 확인
export const majorNoticeQueryOption = () => ({
  mutationKey: ['major_notice'],
  mutationFn: async () =>
    await fetch.post<MajorNoticeSpace.Request, MajorNoticeSpace.Response>(CMDTYPES.MajorNotice),
})

// 푸시토큰 업데이트
export const pushTokenUpdateQueryOption = () => ({
  mutationKey: ['push_token_update'],
  mutationFn: async (data?: PushTokenUpdateSpace.Request) =>
    await fetch.post<PushTokenUpdateSpace.Request, PushTokenUpdateSpace.Response>(
      CMDTYPES.PushTokenUpdate,
      data,
    ),
})
//인증번호 발송
export const otpCertRequestQueryOption = () => ({
  mutationKey: ['otp_cert_request'],
  mutationFn: async (data?: OtpCertRequestSpace.Request) =>
    fetch.post<OtpCertRequestSpace.Request, OtpCertRequestSpace.Response>(
      CMDTYPES.OtpCertRequest,
      data,
    ),
})
// 인증번호 검증
export const otpCertSubmitQueryOption = () => ({
  mutationKey: ['otp_cert_submit'],
  mutationFn: async (data?: OtpCertSubmitSpace.Request) =>
    fetch.post<OtpCertSubmitSpace.Request, OtpCertSubmitSpace.Response>(
      CMDTYPES.OtpCertSubmit,
      data,
    ),
})
// 서비스 가입[약관] (otp transId version)
export const joinSubmitQueryOption = () => ({
  mutationKey: ['join_submit'],
  mutationFn: async (data?: JoinSpace.Request) =>
    fetch.post<JoinSpace.Request, JoinSpace.Response>(CMDTYPES.Join, data),
})
// 인증번호 검증 + 서비스 가입[약관] (otp transId version)
export const joinWithOtpCertSubmitQueryOption = () => ({
  mutationKey: ['join_with_otp_cert_submit'],
  mutationFn: async (data?: JoinWithOtpCertSubmitSpace.Request) =>
    fetch.post<JoinWithOtpCertSubmitSpace.Request, JoinWithOtpCertSubmitSpace.Response>(
      CMDTYPES.JoinWithOtpCertSubmit,
      data,
    ),
})
// 추천검색어
export const searchRecommendWordQueryOption = () => ({
  mutationKey: ['search_recommend_word'],
  mutationFn: async (data?: RecommendSearchWordListSpace.Request) =>
    fetch.post<RecommendSearchWordListSpace.Request, RecommendSearchWordListSpace.Response>(
      CMDTYPES.SearchRecommendWord,
      data,
    ),
  onMutate: async () => {
    const oldData = queryClient.getQueryData(['search_recommend_word'])
    // 우리 update overwrite하지 않기 위해 미리 취소
    await queryClient.cancelQueries({ queryKey: ['search_recommend_word'] })
    // 미리 UI에 적용시켜 놓음
    queryClient.setQueryData(['search_recommend_word'], '')
    // 만약 에러나서 롤백 되면 이전 것을 써놓음.
    return () => queryClient.setQueryData(['search_recommend_word'], oldData)
  },
  onError: (error: any, variable: any, rollback: any) => {
    if (rollback) rollback()
    else console.error(error)
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['search_recommend_word'] })
  },
})
// 로그아웃
export const logoutQueryOption = () => ({
  mutationKey: ['logout'],
  mutationFn: async () => fetch.post<LogoutSpace.Request, LogoutSpace.Response>(CMDTYPES.Logout),
})
// 서비스 해지
export const cancelQueryOption = () => ({
  mutationKey: ['cancel'],
  mutationFn: async () => fetch.post<CancelSpace.Request, CancelSpace.Response>(CMDTYPES.Cancel),
})

// PG사(billgate or danal)
export const paymentGatewayQueryOption = () => ({
  mutationKey: ['payment_gateway'],
  mutationFn: async (data?: PaymentGatewayRequestSpace.Request) =>
    fetch.post<PaymentGatewayRequestSpace.Request, PaymentGatewayRequestSpace.Response>(
      CMDTYPES.PaymentGatewayRequest,
      data,
    ),
})

// 결제창 호출전 서버에 정보 저장
export const paymentWindowCreditCardRequestQueryOption = () => ({
  mutationKey: ['payment_window_creditcard'],
  mutationFn: async (data?: PaymentWindowRequestCreditCardSpace.BillgateRequest) =>
    fetch.post<
      | PaymentWindowRequestCreditCardSpace.BillgateRequest
      | PaymentWindowRequestCreditCardSpace.DanalRequest,
      PaymentWindowRequestCreditCardSpace.Response
    >(CMDTYPES.PaymentWindowCreditCardRequest, data),
})

// 결제창 호출전 서버에 정보 저장 - 선물용
export const giftPaymentWindowCreditCardRequestQueryOption = () => ({
  mutationKey: ['gift_payment_window_creditcard'],
  mutationFn: async (data?: GiftPaymentWindowRequestCreditCardSpace.Request) =>
    fetch.post<
      GiftPaymentWindowRequestCreditCardSpace.Request,
      GiftPaymentWindowRequestCreditCardSpace.Response
    >(CMDTYPES.GiftPaymentWindowCreditCardRequest, data),
})

// 결제창 응답
export const paymentWindowResponseQueryOption = () => ({
  mutationKey: ['payment_window_response'],
  mutationFn: async (data?: PaymentWindowResponseSpace.Request) =>
    fetch.post<PaymentWindowResponseSpace.Request, PaymentWindowResponseSpace.Response>(
      CMDTYPES.PaymentWindowCreditCardResponse,
      data,
    ),
})

export const purchaseCancelQueryOption = () => ({
  mutationKey: ['purchase_cancel'],
  mutationFn: async (data?: PurchaseCancelSpace.Request) =>
    fetch.post<PurchaseCancelSpace.Request, PurchaseCancelSpace.Response>(
      CMDTYPES.PurchaseCouponCancel,
      data,
    ),
})
export const refundSubmitQueryOption = () => ({
  mutationKey: ['refund_submit'],
  mutationFn: async (data?: CouponRefundSpace.Request) =>
    fetch.post<CouponRefundSpace.Request, CouponRefundSpace.Response>(CMDTYPES.RefundSubmit, data),
})

export const refundTelecomCertFileQueryOption = () => ({
  mutationKey: ['refund_cert_telecom_file_submit'],
  mutationFn: async (data?: RefundTelecomCertSubmitFileSpace.Request) =>
    fetch.post<RefundTelecomCertSubmitFileSpace.Request, RefundTelecomCertSubmitFileSpace.Response>(
      CMDTYPES.RefundTelecomCertFileSubmit,
      data,
    ),
})

export const notifyUpdateSubmitQueryOption = () => ({
  mutationKey: ['notify_received_update'],
  mutationFn: async (data?: NotifyUpdateSpace.Request) =>
    fetch.post<NotifyUpdateSpace.Request, NotifyUpdateSpace.Response>(
      CMDTYPES.NotificationReceivedUpdate,
      data,
    ),
})

export const selfRegisterCouponImageUploadQueryOption = () => ({
  mutationKey: ['self_register_image_upload'],
  mutationFn: async (data?: SelfRegisterCouponImageUploadSpace.Request) =>
    fetch.post<
      SelfRegisterCouponImageUploadSpace.Request,
      SelfRegisterCouponImageUploadSpace.Response
    >(CMDTYPES.SelfRegisterCouponImageUpload, data),
})
export const selfRegisterCouponCreateQueryOption = () => ({
  mutationKey: ['self_register_create'],
  mutationFn: async (data?: SelfRegisterCouponCreateSpace.Request) =>
    fetch.post<SelfRegisterCouponCreateSpace.Request, SelfRegisterCouponCreateSpace.Response>(
      CMDTYPES.SelfRegisterCouponCreate,
      data,
    ),
})
export const selfRegisterCouponUpdateQueryOption = () => ({
  mutationKey: ['self_register_update'],
  mutationFn: async (data?: SelfRegisterCouponUpdateSpace.Request) =>
    fetch.post<SelfRegisterCouponUpdateSpace.Request, SelfRegisterCouponUpdateSpace.Response>(
      CMDTYPES.SelfRegisterCouponUpdate,
      data,
    ),
})
export const selfRegisterCouponDeleteQueryOption = () => ({
  mutationKey: ['self_register_delete'],
  mutationFn: async (data?: SelfRegisterCouponDeleteSpace.Request) =>
    fetch.post<SelfRegisterCouponDeleteSpace.Request, SelfRegisterCouponDeleteSpace.Response>(
      CMDTYPES.SelfRegisterCouponDelete,
      data,
    ),
})
export const selfRegisterCouponImageDeleteQueryOption = () => ({
  mutationKey: ['self_register_image_delete'],
  mutationFn: async (data?: SelfRegisterCouponImageDeleteSpace.Request) =>
    fetch.post<
      SelfRegisterCouponImageDeleteSpace.Request,
      SelfRegisterCouponImageDeleteSpace.Response
    >(CMDTYPES.SelfRegisterCouponImageDelete, data),
})
export const favoriteUpdateQueryOption = () => ({
  mutationKey: ['favorite_update'],
  mutationFn: async (data?: FavoriteStarSpace.Request) =>
    fetch.post<FavoriteStarSpace.Request, FavoriteStarSpace.Response>(
      CMDTYPES.FavoriteUpdate,
      data,
    ),
})

export const purchaseValidCheckQueryOption = () => ({
  mutationKey: ['purchase_valid_check'],
  mutationFn: async (data?: PurchaseValidCheckSpace.Request) =>
    fetch.post<PurchaseValidCheckSpace.Request, PurchaseValidCheckSpace.Response>(
      CMDTYPES.PurchaseValidCheck,
      data,
    ),
})

export const ktMVNOCheckQueryOption = () => ({
  mutationKey: ['kt_MVNO_Check'],
  mutationFn: async (data?: KTMVNOCheckSpace.Request) =>
    fetch.post<KTMVNOCheckSpace.Request, KTMVNOCheckSpace.Response>(CMDTYPES.KTMVNO, data),
})

export const kakaomapInfoAuthUpdate = () => ({
  mutationKey: ['kakaomap_info_auth_update'],
  mutationFn: async (data?: KakaoMapInfoAuthUpdateSpace.Request) =>
    fetch.post<KakaoMapInfoAuthUpdateSpace.Request, KakaoMapInfoAuthUpdateSpace.Response>(
      CMDTYPES.KAKAOMAPINFOAUTHUPDATE,
      data,
    ),
})

//카카오맵 위치정보 사용동의
export const kakaomapInfoAuthStatusQueryOption = () => ({
  mutationKey: ['kakao_map_info_auth_status'],
  mutationFn: () =>
    fetch.post<KakaoMapInfoAuthStatusSpace.Request, KakaoMapInfoAuthStatusSpace.Response>(
      CMDTYPES.KAKAOMAPINFOAUTH,
      {},
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

//PASS데이터 암호화 요청
export const passDataEncryptRequestQueryOption = () => ({
  mutationKey: ['pass_data_encrypt_request'],
  mutationFn: async (data?: PassDataEncryptRequestSpace.Request) =>
    fetch.post<PassDataEncryptRequestSpace.Request, PassDataEncryptRequestSpace.Response>(
      CMDTYPES.PassDataEncryptRequest,
      data,
    ),
})

//PASS 본인인증 결과확인
export const passCertResponseQueryOption = () => ({
  mutationKey: ['pass_cert_response'],
  mutationFn: async (data?: PassCertResponseSpace.Request) =>
    fetch.post<PassCertResponseSpace.Request, PassCertResponseSpace.Response>(
      CMDTYPES.PassCertResponse,
      data,
    ),
})

//error message check
export const errMsgCheckResponseQueryOption = () => ({
  mutationKey: ['err_msg_check'],
  mutationFn: async (data?: CouponCancelValidSpace.Request) =>
    fetch.post<CouponCancelValidSpace.Request, CouponCancelValidSpace.Response>(
      CMDTYPES.CancelCouponValidCheck,
      data,
    ),
})

export const giftSendCancelQueryOption = () => ({
  mutationKey: ['gift_send_cancel'],
  mutationFn: async (data?: GiftSendCancelSpace.Request) =>
    fetch.post<GiftSendCancelSpace.Request, GiftSendCancelSpace.Response>(
      CMDTYPES.GiftSendCancel,
      data,
    ),
})

export const giftSendPurchasedQueryOption = () => ({
  mutationKey: ['gift_send_purchased'],
  mutationFn: async (data?: GiftSendPurchasedSpace.Request) =>
    fetch.post<GiftSendPurchasedSpace.Request, GiftSendPurchasedSpace.Response>(
      CMDTYPES.GiftSendPurchased,
      data,
    ),
})

export const giftReceiveAcceptQueryOption = () => ({
  mutationKey: ['gift_receive_accept'],
  mutationFn: async (data?: GiftReceiveAcceptSpace.Request) =>
    fetch.post<GiftReceiveAcceptSpace.Request, GiftReceiveAcceptSpace.Response>(
      CMDTYPES.GiftReceiveAccept,
      data,
    ),
})

export const giftReceiveRejectQueryOption = () => ({
  mutationKey: ['gift_receive_accept'],
  mutationFn: async (data?: GiftReceiveRejectSpace.Request) =>
    fetch.post<GiftReceiveRejectSpace.Request, GiftReceiveRejectSpace.Response>(
      CMDTYPES.GiftReceiveReject,
      data,
    ),
})

export const giftRefundSubmitQueryOption = () => ({
  mutationKey: ['gift_refund_submit'],
  mutationFn: async (data?: GiftRefundSpace.Request) =>
    fetch.post<GiftRefundSpace.Request, GiftRefundSpace.Response>(CMDTYPES.GiftRefundSubmit, data),
})

export const vulerableCheckQueryOption = () => ({
  mutationKey: ['check_vulerable'],
  mutationFn: async (data?: VulerableCheckSpace.Request) =>
    fetch.post<VulerableCheckSpace.Request, VulerableCheckSpace.Response>(
      CMDTYPES.CheckVulerable,
      data,
    ),
})

export const sessionCheckQueryOption = () => ({
  mutationKey: ['session_check'],
  mutationFn: async () => fetch.post<unknown, unknown>(CMDTYPES.SessionCheck),
})
